import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const TRANSFERS_HINT_ID = 'quickplay-stepper-transfers-hint';
const TRANSFERS_HINT = 'Finish your match to open transfers.';

const TEAM_ROUTE_PATTERN = /\/team-(?:pitch|list|settings)(?:\/|$)/;
const MATCH_ROUTE_PATTERN = /\/match\/[^/]+/;
const TRANSFERS_ROUTE_PATTERN = /\/transfers/;

function CheckIcon() {
    return (
        <svg viewBox="0 0 12 10" width="11" height="9" aria-hidden="true" focusable="false">
            <path
                d="M1.4 5.4 4.5 8.3 10.6 1.7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

// The stepper takes the place of the main menu while a Quick Play manager walks
// through the first loop: pick the team, play the match, then open transfers.
function QuickplayStepper({
    leagueSlug,
    currentTeamId = null,
    matchId = null,
    isMatchStarted = false,
    isMatchCompleted = false
}) {
    const { pathname } = useLocation();

    if (!leagueSlug) {
        return null;
    }

    const isTeamRoute = TEAM_ROUTE_PATTERN.test(pathname);
    const isMatchRoute = MATCH_ROUTE_PATTERN.test(pathname);
    const isTransfersRoute = TRANSFERS_ROUTE_PATTERN.test(pathname);
    const isTransfersUnlocked = isMatchStarted
        || isMatchCompleted
        || isMatchRoute;

    // "active" marks the page the manager is on, "next" is the step we want them
    // to take now, "complete" is behind them and "upcoming" is not open yet.
    let matchState = 'upcoming';
    if (isMatchRoute) {
        matchState = 'active';
    } else if (isMatchCompleted) {
        matchState = 'complete';
    } else if (matchId) {
        matchState = 'next';
    }

    let transfersState = 'upcoming';
    if (isTransfersRoute) {
        transfersState = 'active';
    } else if (isMatchRoute || isMatchCompleted) {
        transfersState = 'next';
    }

    const steps = [
        {
            key: 'start',
            label: 'Start',
            state: 'complete',
            to: null
        },
        {
            key: 'team',
            label: 'My Team',
            state: isTeamRoute ? 'active' : 'complete',
            to: currentTeamId ? `/${leagueSlug}/team-pitch/${currentTeamId}` : null
        },
        {
            key: 'match',
            label: 'Match',
            state: matchState,
            to: matchId ? `/${leagueSlug}/match/${matchId}/match` : null
        },
        {
            key: 'transfers',
            label: 'Transfers',
            state: transfersState,
            to: isTransfersUnlocked ? `/${leagueSlug}/transfers-players` : null,
            locked: !isTransfersUnlocked,
            // Reaching Transfers finishes the loop, so the step shows a green
            // check even though it is also the current page.
            done: transfersState === 'active' && isMatchCompleted,
            hintId: TRANSFERS_HINT_ID
        }
    ];

    // The actionable next step renders as a real pill button in its slot on
    // the progress line, so the manager always has one obvious control to
    // press and the bar never changes size between states.
    const renderStepBody = (step, index) => {
        if (step.state === 'next') {
            return (
                <>
                    <span className="quickplay-stepper-label">{step.label}</span>
                    <span
                        className="ico icon-arrow-right quickplay-stepper-label-arrow"
                        aria-hidden="true"
                    ></span>
                </>
            );
        }

        return (
            <>
                <span className="quickplay-stepper-node" aria-hidden="true">
                    {step.state === 'complete' || step.done ? <CheckIcon /> : index + 1}
                </span>
                <span className="quickplay-stepper-label">{step.label}</span>
            </>
        );
    };

    const renderStep = (step, index) => {
        if (step.to) {
            return (
                <Link className="quickplay-stepper-link" to={step.to}>
                    {renderStepBody(step, index)}
                </Link>
            );
        }

        // Step 1 is a milestone the manager has already passed, so it is plain
        // text rather than a control they can reach with the keyboard.
        if (step.key === 'start') {
            return (
                <span className="quickplay-stepper-link">
                    {renderStepBody(step, index)}
                </span>
            );
        }

        return (
            <button
                type="button"
                className="quickplay-stepper-link"
                disabled
                aria-describedby={step.hintId}
            >
                {renderStepBody(step, index)}
            </button>
        );
    };

    return (
        <nav className="quickplay-stepper" aria-label="Quick Play steps">
            <ol className="quickplay-stepper-list">
                {steps.map((step, index) => (
                    <li
                        key={step.key}
                        className={`quickplay-stepper-step ${step.state}${step.locked ? ' locked' : ''}${step.done ? ' done' : ''}`}
                        aria-current={step.state === 'active' ? 'step' : undefined}
                    >
                        {index > 0 && (
                            <span
                                className={`quickplay-stepper-line${
                                    steps[index - 1].state === 'complete' ? ' is-filled' : ''
                                }`}
                                aria-hidden="true"
                            ></span>
                        )}

                        {renderStep(step, index)}

                        {step.key === 'transfers' && step.locked && (
                            <span className="sr-only" id={TRANSFERS_HINT_ID}>
                                {TRANSFERS_HINT}
                            </span>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    );
}

export { QuickplayStepper, TRANSFERS_HINT };
